import { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

interface ProgressData {
  currentSubStep: number;
  totalSubSteps: number;
  formData: any;
  status: "draft" | "submitted" | "completed";
  lastSaved: number;
}

interface UseWizardStateReturn {
  startIndex: number;
  isReady: boolean;
  initializeWizard: () => void;
}

export const calculateInitialStep = (
  firebaseDraft: ProgressData | null,
  totalSteps: number
): number => {
  if (!firebaseDraft) {
    return 0;
  }

  if (firebaseDraft.status === "completed") {
    return firebaseDraft.currentSubStep;
  }

  const nextStep = (firebaseDraft.currentSubStep ?? -1) + 1;
  return Math.min(nextStep, totalSteps - 1);
};

export const shouldRestoreFormData = (
  firebaseDraft: ProgressData | null
): boolean => {
  return !!firebaseDraft?.formData;
};

export const useWizardState = (
  firebaseDraft: ProgressData | null,
  draftLoaded: boolean,
  totalSteps: number,
  formMethods: UseFormReturn<any>
): UseWizardStateReturn => {
  const [startIndex, setStartIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const initializeWizard = () => {
    if (!draftLoaded) {
      return;
    }

    const stepToShow = calculateInitialStep(firebaseDraft, totalSteps);

    if (shouldRestoreFormData(firebaseDraft)) {
      formMethods.reset(firebaseDraft!.formData);
    }

    setStartIndex(stepToShow);
    setIsReady(true);
  };

  useEffect(() => {
    initializeWizard();
  }, [draftLoaded, firebaseDraft, formMethods.reset]);

  return {
    startIndex,
    isReady,
    initializeWizard,
  };
};
